// client/src/components/User/AddFriendModal.jsx
import React, { useCallback, useEffect, useState } from "react";
import {
  searchUsersForFriend,
  sendFriendRequest,
  getIncomingFriendRequests,
  acceptFriendRequest,
} from "../../services/api";
import {
  normalizeIncomingRequestsPayload,
  normalizeSearchUsersPayload,
  isCurrentUser,
  sameIdentity,
  formatShortDate,
} from "../Achievements/achievementsConfig";

const AddFriendModal = React.memo(function AddFriendModal({
  isOpen,
  onClose,
  myIdentity,
  incomingCount,
  setIncomingCount,
  onFriendAccepted,
}) {
  const [modalTab, setModalTab] = useState("find");
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchingUsers, setSearchingUsers] = useState(false);
  const [sendingToId, setSendingToId] = useState("");
  const [requestsLoading, setRequestsLoading] = useState(false);
  const [friendRequests, setFriendRequests] = useState([]);
  const [acceptingRequestId, setAcceptingRequestId] = useState("");
  const [friendActionError, setFriendActionError] = useState("");

  const loadIncomingRequests = useCallback(async () => {
    try {
      setRequestsLoading(true);
      setFriendActionError("");

      const res = await getIncomingFriendRequests();
      const requests = normalizeIncomingRequestsPayload(res);

      setFriendRequests(requests);
      setIncomingCount(requests.length);
    } catch (e) {
      console.error(e);
      setFriendActionError("Failed to load friend requests.");
    } finally {
      setRequestsLoading(false);
    }
  }, [setIncomingCount]);

  useEffect(() => {
    if (!isOpen) return;
    loadIncomingRequests();
  }, [isOpen, loadIncomingRequests]);

  const handleFriendSearch = useCallback(async () => {
    const q = searchTerm.trim();
    if (!q) {
      setSearchResults([]);
      return;
    }

    try {
      setSearchingUsers(true);
      setFriendActionError("");

      const res = await searchUsersForFriend(q);
      const rawUsers = normalizeSearchUsersPayload(res);
      const filtered = rawUsers.filter((u) => !isCurrentUser(u, myIdentity));

      setSearchResults(filtered);
    } catch (e) {
      console.error(e);
      setFriendActionError("Unable to search users right now.");
      setSearchResults([]);
    } finally {
      setSearchingUsers(false);
    }
  }, [myIdentity, searchTerm]);

  const handleSendFriendRequest = useCallback(async (targetUser) => {
    const targetUserId = targetUser?._id || targetUser?.id || targetUser?.userId;
    if (!targetUserId) return;

    try {
      setSendingToId(String(targetUserId));
      setFriendActionError("");

      await sendFriendRequest(targetUserId);

      setSearchResults((prev) =>
        prev.map((item) =>
          sameIdentity(item, targetUser)
            ? { ...item, requestSent: true, friendStatus: "pending_outgoing" }
            : item
        )
      );
    } catch (e) {
      console.error(e);
      setFriendActionError("Failed to send friend request.");
    } finally {
      setSendingToId("");
    }
  }, []);

  const handleAcceptFriendRequest = useCallback(
    async (requestObjOrId) => {
      const requestId =
        typeof requestObjOrId === "string"
          ? requestObjOrId
          : requestObjOrId?._id || requestObjOrId?.id || requestObjOrId?.requestId;

      if (!requestId) return;

      const acceptedReq =
        typeof requestObjOrId === "object"
          ? requestObjOrId
          : friendRequests.find((req) => {
              const rid = req?._id || req?.id || req?.requestId;
              return String(rid) === String(requestId);
            });

      const acceptedSender =
        acceptedReq?.from || acceptedReq?.sender || acceptedReq?.user || acceptedReq?.requester || null;

      try {
        setAcceptingRequestId(String(requestId));
        setFriendActionError("");

        await acceptFriendRequest(requestId);

        const nextRequests = friendRequests.filter((req) => {
          const rid = req?._id || req?.id || req?.requestId;
          return String(rid) !== String(requestId);
        });

        setFriendRequests(nextRequests);
        setIncomingCount(nextRequests.length);

        if (acceptedSender) {
          setSearchResults((prev) =>
            prev.map((item) =>
              sameIdentity(item, acceptedSender)
                ? {
                    ...item,
                    isFriend: true,
                    friendStatus: "accepted",
                    requestSent: false,
                    requestReceived: false,
                  }
                : item
            )
          );
        }

        onFriendAccepted?.(acceptedSender);
      } catch (e) {
        console.error(e);
        setFriendActionError("Failed to accept friend request.");
      } finally {
        setAcceptingRequestId("");
      }
    },
    [friendRequests, onFriendAccepted, setIncomingCount]
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/60 backdrop-blur-sm transition-all duration-300 animate-[fadeIn_0.3s_ease-out]">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg p-6 relative animate-bounce-in max-h-[85vh] overflow-hidden flex flex-col">
        <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 z-10">
          <i className="fa-solid fa-xmark text-xl"></i>
        </button>

        <h3 className="text-lg font-heading font-bold text-gray-900 mb-1 text-center">Add Study Buddy</h3>
        <p className="text-xs text-gray-500 text-center mb-5">Search friends or accept requests</p>

        <div className="flex bg-gray-100 rounded-xl p-1 mb-4">
          <button
            onClick={() => setModalTab("find")}
            className={`flex-1 py-2 rounded-lg text-sm font-bold transition ${
              modalTab === "find" ? "bg-white text-indigo-600 shadow-sm" : "text-gray-500"
            }`}
          >
            Find Friends
          </button>

          <button
            onClick={async () => {
              setModalTab("requests");
              await loadIncomingRequests();
            }}
            className={`flex-1 py-2 rounded-lg text-sm font-bold transition relative ${
              modalTab === "requests" ? "bg-white text-indigo-600 shadow-sm" : "text-gray-500"
            }`}
          >
            Requests
            {incomingCount > 0 && (
              <span className="absolute -top-1 right-3 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] flex items-center justify-center">
                {incomingCount}
              </span>
            )}
          </button>
        </div>

        {friendActionError && (
          <div className="mb-4 rounded-xl border border-red-100 bg-red-50 px-3 py-2 text-xs text-red-600 font-medium">
            {friendActionError}
          </div>
        )}

        <div className="flex-1 overflow-y-auto pr-1">
          {modalTab === "find" && (
            <div>
              <div className="flex gap-2 mb-4">
                <div className="relative flex-1">
                  <i className="fa-solid fa-magnifying-glass absolute left-3 top-3 text-gray-400 text-sm"></i>
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleFriendSearch();
                    }}
                    placeholder="Enter username or email..."
                    className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-gray-200 focus:border-indigo-500 outline-none text-sm bg-gray-50 focus:ring-2 focus:ring-indigo-100 transition-all"
                  />
                </div>

                <button
                  onClick={handleFriendSearch}
                  disabled={searchingUsers}
                  className="bg-indigo-600 text-white font-bold px-4 py-2.5 rounded-xl hover:bg-indigo-700 transition text-sm shadow-md shadow-indigo-200 disabled:opacity-60"
                >
                  {searchingUsers ? "Searching..." : "Search"}
                </button>
              </div>

              <div className="space-y-3">
                {!searchingUsers && searchTerm.trim() && searchResults.length === 0 && (
                  <div className="rounded-2xl border border-dashed border-gray-200 bg-gray-50 p-6 text-center">
                    <p className="text-sm font-semibold text-gray-700">No users found</p>
                    <p className="text-xs text-gray-500 mt-1">Try a different username or email.</p>
                  </div>
                )}

                {!searchTerm.trim() && (
                  <div className="rounded-2xl border border-dashed border-gray-200 bg-gray-50 p-6 text-center">
                    <p className="text-sm font-semibold text-gray-700">Search for a study buddy</p>
                    <p className="text-xs text-gray-500 mt-1">Type a username or email to send a friend request.</p>
                  </div>
                )}

                {searchResults.map((person) => {
                  const personId = person?._id || person?.id || person?.userId;
                  const isFriend = person?.isFriend || person?.friendStatus === "accepted";
                  const requestSent = person?.requestSent || person?.friendStatus === "pending_outgoing";

                  const matchedIncomingRequest = friendRequests.find((req) => {
                    const sender = req?.from || req?.sender || req?.user || req?.requester || {};
                    return sameIdentity(sender, person);
                  });

                  const requestReceived =
                    person?.requestReceived ||
                    person?.friendStatus === "pending_incoming" ||
                    Boolean(matchedIncomingRequest);

                  const requestId =
                    matchedIncomingRequest?._id ||
                    matchedIncomingRequest?.id ||
                    matchedIncomingRequest?.requestId;

                  return (
                    <div
                      key={personId || person.email}
                      className="flex items-center gap-3 rounded-2xl border border-gray-100 p-3 bg-white shadow-sm"
                    >
                      <img
                        src={
                          person?.avatar ||
                          `https://api.dicebear.com/9.x/adventurer/svg?seed=${encodeURIComponent(
                            person?.firstName || person?.email || "User"
                          )}&backgroundColor=b6e3f4`
                        }
                        alt={person?.firstName || "User"}
                        className="w-12 h-12 rounded-full object-cover border border-gray-200 bg-gray-100"
                      />

                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold text-gray-800 truncate">
                          {person?.firstName || "User"} {person?.lastName || ""}
                        </p>
                        <p className="text-xs text-gray-500 truncate">{person?.email || "No email"}</p>
                      </div>

                      {isFriend ? (
                        <span className="px-3 py-1.5 rounded-lg text-xs font-bold bg-green-50 text-green-600 border border-green-100">
                          Friends
                        </span>
                      ) : requestReceived ? (
                        <button
                          onClick={() =>
                            requestId ? handleAcceptFriendRequest(requestId) : setModalTab("requests")
                          }
                          disabled={Boolean(requestId) && acceptingRequestId === String(requestId)}
                          className="px-3 py-1.5 rounded-lg text-xs font-bold bg-amber-500 text-white hover:bg-amber-600 transition disabled:opacity-60"
                        >
                          {requestId && acceptingRequestId === String(requestId) ? "Accepting..." : "Accept"}
                        </button>
                      ) : requestSent ? (
                        <span className="px-3 py-1.5 rounded-lg text-xs font-bold bg-gray-100 text-gray-500 border border-gray-200">
                          Requested
                        </span>
                      ) : (
                        <button
                          onClick={() => handleSendFriendRequest(person)}
                          disabled={sendingToId === String(personId)}
                          className="px-3 py-1.5 rounded-lg text-xs font-bold bg-indigo-600 text-white hover:bg-indigo-700 transition disabled:opacity-60"
                        >
                          {sendingToId === String(personId) ? "Sending..." : "Add"}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {modalTab === "requests" && (
            <div className="space-y-3">
              {requestsLoading && (
                <div className="rounded-2xl border border-gray-100 bg-gray-50 p-6 text-center">
                  <p className="text-sm text-gray-500">Loading requests...</p>
                </div>
              )}

              {!requestsLoading && friendRequests.length === 0 && (
                <div className="rounded-2xl border border-dashed border-gray-200 bg-gray-50 p-6 text-center">
                  <p className="text-sm font-semibold text-gray-700">No incoming requests</p>
                  <p className="text-xs text-gray-500 mt-1">When someone adds you, it will appear here.</p>
                </div>
              )}

              {!requestsLoading &&
                friendRequests.map((req) => {
                  const sender = req?.from || req?.sender || req?.user || req?.requester || {};
                  const requestId = req?._id || req?.id || req?.requestId;

                  return (
                    <div
                      key={requestId}
                      className="flex items-center gap-3 rounded-2xl border border-gray-100 p-3 bg-white shadow-sm"
                    >
                      <img
                        src={
                          sender?.avatar ||
                          `https://api.dicebear.com/9.x/adventurer/svg?seed=${encodeURIComponent(
                            sender?.firstName || sender?.email || "User"
                          )}&backgroundColor=b6e3f4`
                        }
                        alt={sender?.firstName || "User"}
                        className="w-12 h-12 rounded-full object-cover border border-gray-200 bg-gray-100"
                      />

                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold text-gray-800 truncate">
                          {sender?.firstName || "User"} {sender?.lastName || ""}
                        </p>
                        <p className="text-xs text-gray-500 truncate">{sender?.email || "No email"}</p>
                        {req?.createdAt && (
                          <p className="text-[11px] text-gray-400 mt-1">
                            Sent {formatShortDate(req.createdAt)}
                          </p>
                        )}
                      </div>

                      <button
                        onClick={() => handleAcceptFriendRequest(req)}
                        disabled={acceptingRequestId === String(requestId)}
                        className="px-3 py-1.5 rounded-lg text-xs font-bold bg-green-600 text-white hover:bg-green-700 transition disabled:opacity-60"
                      >
                        {acceptingRequestId === String(requestId) ? "Accepting..." : "Accept"}
                      </button>
                    </div>
                  );
                })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

export default AddFriendModal;
